// apps/web/src/pages/author/EditorToolbar.tsx
//
// Phase 8 Plan 07 Task 1 — EditorToolbar (authr-01).
//
// AUTHR-06 / RESEARCH §"Pitfall 2" — FORMSTATE DISCIPLINE:
// ----------------------------------------------------------------------
// This file is the ONLY place in apps/web/src/pages/author/ that reads
// react-hook-form `formState`. Reading `formState.isDirty` (or `.isValid`,
// `.isSubmitting`, etc.) subscribes the calling component to EVERY form
// mutation — placing such reads inside a deeply-nested form-section subtree
// blows the AUTHR-06 16ms-per-keystroke budget on first contact.
//
// Form sections (sections/*.tsx) and widget-config sub-forms
// (widget-config/*.tsx) MUST use per-path `useWatch({ name })` — NEVER
// `useFormState()` / `formState.*`. Plan 12 may add a static check
// (`scripts/check-no-toplevel-usewatch.sh`) as a defense-in-depth gate.
//
// COPY: UI-SPEC §"EditorToolbar (Author UI chrome)" copy table (lines 296-315).
// DISCARD: Two-stage armed-confirm pattern verbatim from
//          apps/web/src/widgets/summary-checklist/renderer.tsx lines 75-116
//          (Phase 6 — in production through Phase 7 close with no incident).
// W7: Fixture-load Dialog ships three menu items pinned to the explicit ids
// (high-complexity, low-complexity, mid-complexity per STRESSES.md). Plan 10
// RecipeEditor's onLoadFixture handler resolves these ids to the matching
// fixture JSON imports.
//
// grep-no-recipe-identifiers exception: this file carries user-visible copy
// for the Fixture-load menu. Each line that contains a forbidden identifier
// carries an explicit `// grep-allow: <pattern>` marker.
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useFormState } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ARMED_TIMEOUT_MS = 4000;

export type FixtureId = "pancake" | "one-line" | "cocktail"; // grep-allow: pancake, cocktail

// W7 — fixture menu labels (UI-SPEC §"EditorToolbar" line 309). Extracted as
// constants so each forbidden literal appears on a single line that carries an
// explicit per-pattern grep-allow marker (high-complexity label + high-/mid-
// complexity fixture ids).
const FIXTURE_LABEL_HIGH = "Pancake (high complexity)"; // grep-allow: pancake
const FIXTURE_LABEL_LOW = "One-line (low complexity)";
const FIXTURE_LABEL_MID = "Mid-complexity"; // grep-allow: cocktail
const FIXTURE_ID_HIGH: FixtureId = "pancake"; // grep-allow: pancake
const FIXTURE_ID_LOW: FixtureId = "one-line";
const FIXTURE_ID_MID: FixtureId = "cocktail"; // grep-allow: cocktail

export interface EditorToolbarProps {
  /**
   * Recipe title from form state. Empty string falls back to "Untitled recipe"
   * per UI-SPEC line 299 (existing recipe with empty name). `undefined` means
   * "new recipe" (toolbar reads "New recipe").
   */
  recipeName?: string | undefined;
  onSave: () => void;
  onDiscard: () => void;
  onLoadFixture: (fixtureId: FixtureId) => void;
  onPasteJson: () => void;
  /**
   * WR-09 — additional pending signal from the parent's save mutation.
   * `useFormState().isSubmitting` is only true DURING handleSubmit; the
   * window between handleSubmit returning and useMutation.mutateAsync
   * resolving was previously a hole where rapid double-clicks could fire
   * two writes. Caller passes saveMutation.isPending; the toolbar treats
   * `isSaving || isSubmitting` as the disable signal.
   * Optional for back-compat with existing EditorToolbar callers/tests
   * (default false).
   */
  isSaving?: boolean;
}

export function EditorToolbar(props: EditorToolbarProps): React.ReactElement {
  const { recipeName, onSave, onDiscard, onLoadFixture, onPasteJson, isSaving = false } = props;

  // FORMSTATE DISCIPLINE (see file header comment): toolbar-only reads.
  const { isDirty, isValid, isSubmitting } = useFormState();
  // WR-09 — `submitting` aggregates RHF's transient isSubmitting and the
  // parent's longer-lived useMutation.isPending. This closes the window
  // between handleSubmit returning and the network round-trip resolving,
  // where the Save button would otherwise be enabled.
  const submitting = isSubmitting || isSaving;

  // Two-stage Discard armed-confirm pattern. Copied verbatim from
  // apps/web/src/widgets/summary-checklist/renderer.tsx lines 75-116.
  const [armed, setArmed] = useState<"idle" | "armed">("idle");
  const armedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (armedTimerRef.current !== null) {
        clearTimeout(armedTimerRef.current);
      }
    };
  }, []);

  // Load-fixture Dialog open state (v1 simplicity per plan §Task 1: ship the
  // menu as a Dialog containing three clickable menu items; native menubar
  // primitive not introduced for v1).
  const [fixtureDialogOpen, setFixtureDialogOpen] = useState(false);

  // Save button state machine — UI-SPEC §"EditorToolbar" copy table lines
  // 300-308. aria-label stays "Save recipe" across ALL states (line 304).
  let saveContent: React.ReactNode;
  let saveDisabled: boolean;
  if (submitting) {
    saveContent = (
      <>
        <Loader2 size={14} className="animate-spin" aria-hidden />
        <span>Saving…</span>
      </>
    );
    saveDisabled = true;
  } else if (!isDirty) {
    saveContent = (
      <>
        <Check size={14} className="text-muted-foreground" aria-hidden />
        <span>Saved</span>
      </>
    );
    saveDisabled = true;
  } else if (!isValid) {
    saveContent = (
      <>
        <AlertCircle size={14} className="text-destructive" aria-hidden />
        <span>Save recipe</span>
      </>
    );
    saveDisabled = true;
  } else {
    saveContent = <span>Save recipe</span>;
    saveDisabled = false;
  }

  const title =
    recipeName === undefined
      ? "New recipe"
      : recipeName === ""
        ? "Edit recipe: Untitled recipe"
        : `Edit recipe: ${recipeName}`;

  const onDiscardClick = (): void => {
    if (armed === "armed") {
      if (armedTimerRef.current !== null) {
        clearTimeout(armedTimerRef.current);
        armedTimerRef.current = null;
      }
      onDiscard();
      setArmed("idle");
      return;
    }
    setArmed("armed");
    armedTimerRef.current = setTimeout(() => {
      setArmed("idle");
      armedTimerRef.current = null;
    }, ARMED_TIMEOUT_MS);
  };

  const onPickFixture = (id: FixtureId): void => {
    onLoadFixture(id);
    setFixtureDialogOpen(false);
  };

  return (
    // <header> implicitly has role="banner" at the document root — biome
    // flags an explicit role attribute as redundant; UI-SPEC §"Accessibility"
    // line 882 acknowledges this ("already correct on <header>").
    <header className="bg-background border-b border-border px-4 py-2 flex items-center justify-between gap-3">
      {/* Left side: title (UI-SPEC §"Author-form-specific typography" line 219 — Display) */}
      <h1 className="text-xl font-semibold leading-snug">{title}</h1>

      {/* Right side: action buttons in order */}
      <div className="flex items-center gap-3">
        {/* Discard — ghost variant; armed-state takes destructive bg */}
        <Button
          type="button"
          variant="ghost"
          aria-label="Discard all unsaved changes"
          disabled={!isDirty || submitting}
          onClick={onDiscardClick}
          className={
            armed === "armed"
              ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
              : ""
          }
        >
          Discard changes
        </Button>
        {armed === "armed" ? (
          <span className="sr-only" aria-live="polite">
            Confirm discard
          </span>
        ) : null}

        {/* Load fixture — opens a Dialog with three menu items */}
        <Button type="button" variant="outline" onClick={() => setFixtureDialogOpen(true)}>
          Load fixture
        </Button>

        {/* Paste JSON */}
        <Button type="button" variant="outline" onClick={onPasteJson}>
          Paste JSON…
        </Button>

        {/* Save — the ONLY Button variant="default" in chrome (UI-SPEC §"Primary CTA discipline") */}
        <Button
          type="button"
          variant="default"
          aria-label="Save recipe"
          aria-busy={submitting}
          disabled={saveDisabled}
          onClick={onSave}
        >
          {saveContent}
        </Button>
      </div>

      {/* Load-fixture Dialog — three menu items pinned to ids */}
      <Dialog open={fixtureDialogOpen} onOpenChange={setFixtureDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Load fixture</DialogTitle>
          </DialogHeader>
          <div role="menu" aria-label="Fixture choices" className="flex flex-col gap-2">
            <Button
              type="button"
              role="menuitem"
              variant="outline"
              onClick={() => onPickFixture(FIXTURE_ID_HIGH)}
              className="justify-start"
            >
              {FIXTURE_LABEL_HIGH}
            </Button>
            <Button
              type="button"
              role="menuitem"
              variant="outline"
              onClick={() => onPickFixture(FIXTURE_ID_LOW)}
              className="justify-start"
            >
              {FIXTURE_LABEL_LOW}
            </Button>
            <Button
              type="button"
              role="menuitem"
              variant="outline"
              onClick={() => onPickFixture(FIXTURE_ID_MID)}
              className="justify-start"
            >
              {FIXTURE_LABEL_MID}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}
